import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { Id, Page } from '@contracts/common';
import type { BatchDetailDTO, BatchRowDTO, BatchRowPatchBody } from '@contracts/ingest';
import { ApiClientError } from '../../../api/client';
import { notify } from '../../../lib/toast';
import { batchKey, patchRow } from './api';

/** §8.1: a field edit is sent once the typing has paused this long. */
export const EDIT_DEBOUNCE_MS = 400;
export const ROW_CONFLICT = 'Dòng đã thay đổi, hãy tải lại';
const FALLBACK = 'Không lưu được dòng này. Hãy thử lại.';

type Change = Omit<BatchRowPatchBody, 'version'>;
export type RowError = { field: string; message: string };

/** The row's fields with the person's edits over them; an emptied field leaves the object (no "" sent). */
export function mergeFields(base: Record<string, unknown>, edits: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base, ...edits };
  for (const [k, v] of Object.entries(edits)) if (v === '' || v === null || v === undefined) delete out[k];
  return out;
}

/** Per job: each mounted row's `flush`, and the writes on their way. */
const flushers = new Map<Id, Set<() => void>>();
const inflight = new Map<Id, Set<Promise<void>>>();
const memberOf = <T,>(map: Map<Id, Set<T>>, jobId: Id): Set<T> => {
  let set = map.get(jobId);
  if (set === undefined) map.set(jobId, (set = new Set()));
  return set;
};

/** Per row: the one queue of its writes and its newest copy, shared by every writer mounted for it (L4 F1). */
type RowShare = { chain: Promise<void>; latest: BatchRowDTO; users: number };
const shares = new Map<string, RowShare>();
const shareKey = (jobId: Id, rowId: Id): string => `${jobId}/${rowId}`;
/** The row's share, made on first sight; counting its writers is the effect's (render may run twice). */
function shareOf(jobId: Id, row: BatchRowDTO): RowShare {
  const key = shareKey(jobId, row.id);
  let share = shares.get(key);
  if (share === undefined) shares.set(key, (share = { chain: Promise.resolve(), latest: row, users: 0 }));
  return share;
}
function joinRow(jobId: Id, rowId: Id, share: RowShare): () => void {
  const key = shareKey(jobId, rowId);
  if (!shares.has(key)) shares.set(key, share);
  share.users += 1;
  return () => {
    share.users -= 1;
    // Forgotten once nobody writes this row and its last write has landed. The hook joins AFTER its flushers effect,
    // so on unmount (cleanups run in declaration order) the unmount's flush is already on `chain` here (L4 N1).
    void share.chain.finally(() => {
      if (share.users === 0 && shares.get(key) === share) shares.delete(key);
    });
  };
}

/**
 * Sends what every row of the job still holds and waits for all its writes (§8.5): Duyệt reads the job's version
 * after the last edit landed, so the apply never races a PATCH nor writes the text before it.
 */
export async function settleWrites(jobId: Id): Promise<void> {
  for (const flush of [...(flushers.get(jobId) ?? [])]) flush();
  await Promise.all([...(inflight.get(jobId) ?? [])]);
}

/**
 * One row's writes (§8.3): every `PATCH` carries the row's newest version and goes after the one before it, so two
 * quick edits never race on one version. Field edits wait `EDIT_DEBOUNCE_MS` and are sent as the WHOLE `fields`
 * object (the server replaces it), merged at send time over what the server last answered; `flush` sends what is
 * waiting at once (blur, a check, a pick) and runs on unmount, so nothing typed is lost before Duyệt. Each answer
 * goes into the card's cache with the children the server deselected (D93), then the job is asked again for its
 * counts (D110).
 */
export function useRowWriter(jobId: Id, row: BatchRowDTO) {
  const queryClient = useQueryClient();
  // One writer per row (L4 F1): every instance for this row (the panel card, the Map ghost's ✕) shares one queue and
  // the row's newest copy, so a write from either side waits for the other's and carries the version it left.
  const shared = useMemo(() => shareOf(jobId, row), [jobId, row.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const latest = useMemo(() => ({
    get current(): BatchRowDTO { return shared.latest; },
    set current(next: BatchRowDTO) { shared.latest = next; },
  }), [shared]);
  // A refetch of the card brings this row back; an older copy than the last answer is ignored.
  useEffect(() => {
    if (row.version >= latest.current.version) latest.current = row;
  }, [row, latest]);
  const pending = useRef<Record<string, unknown>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<RowError | null>(null);
  const [writing, setWriting] = useState(0);

  const enqueue = useCallback((change: () => Change, settle?: (ok: boolean) => void) => {
    setWriting((n) => n + 1);
    shared.chain = shared.chain.then(async () => {
      const current = latest.current;
      try {
        const answer = await patchRow(jobId, current.id, { version: current.version, ...change() }, crypto.randomUUID());
        latest.current = answer.row;
        const out = new Set(answer.deselected);
        const swap = (x: BatchRowDTO) => (x.id === answer.row.id ? answer.row : out.has(x.id) ? { ...x, included: false } : x);
        queryClient.setQueryData<BatchDetailDTO>(batchKey(jobId), (old) => old && ({
          ...old, rows: { ...old.rows, items: old.rows.items.map(swap) },
        }));
        // The page's pages of rows (§8.2), whatever their filter: the row stays where the person is editing it.
        queryClient.setQueriesData<InfiniteData<Page<BatchRowDTO>, string | null>>({ queryKey: [...batchKey(jobId), 'rows'] },
          (old) => old && { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map(swap) })) });
        setError(null);
        settle?.(true);
      } catch (e) {
        if (e instanceof ApiClientError && e.code === 'VERSION_CONFLICT') {
          if (e.currentVersion !== undefined) latest.current = { ...latest.current, version: e.currentVersion };
          notify.warning(ROW_CONFLICT);
        } else if (e instanceof ApiClientError && e.status === 422) {
          const [field, message] = Object.entries(e.fields ?? {})[0] ?? ['', e.message];
          setError({ field, message });
        } else {
          notify.error(e instanceof ApiClientError && e.message !== '' ? e.message : FALLBACK);
        }
        settle?.(false);
      } finally {
        setWriting((n) => n - 1);
        // The job again for its counts (D110); the page's rows were answered above, not read again on each check.
        void queryClient.invalidateQueries({ queryKey: batchKey(jobId), exact: true });
      }
    });
    const write = shared.chain;
    const writes = memberOf(inflight, jobId);
    writes.add(write);
    void write.finally(() => writes.delete(write));
  }, [jobId, queryClient, shared, latest]);

  const flush = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    const edits = pending.current;
    if (Object.keys(edits).length === 0) return;
    pending.current = {};
    enqueue(() => ({ fields: mergeFields(latest.current.fields, edits) }), () => {
      // The field now reads what the server holds (saved, or refused and refetched), unless typed again meanwhile.
      setDraft((d) => {
        const next = { ...d };
        for (const [k, v] of Object.entries(edits)) if (next[k] === v) delete next[k];
        return next;
      });
    });
  }, [enqueue, latest]);

  const edit = useCallback((field: string, value: unknown) => {
    pending.current = { ...pending.current, [field]: value };
    setDraft((d) => ({ ...d, [field]: value }));
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(flush, EDIT_DEBOUNCE_MS);
  }, [flush]);

  // Leaving the card (closed panel, another conversation) still sends what was typed.
  const flushRef = useRef(flush);
  useEffect(() => { flushRef.current = flush; }, [flush]);
  useEffect(() => {
    const flushNow = () => flushRef.current();
    const rows = memberOf(flushers, jobId);
    rows.add(flushNow);
    return () => {
      rows.delete(flushNow);
      flushNow();
    };
  }, [jobId]);
  // Declared after the flushers effect on purpose: its cleanup must see the unmount's flush already queued (L4 N1).
  useEffect(() => joinRow(jobId, row.id, shared), [jobId, row.id, shared]);

  /** Anything else (a check, a pick, the op): what is typed goes first, then this. */
  const send = useCallback((change: Change, settle?: (ok: boolean) => void) => {
    flush();
    setError(null);
    enqueue(() => change, settle);
  }, [flush, enqueue]);

  const value = (field: string): unknown => (field in draft ? draft[field] : row.fields[field]);
  return { value, edit, flush, send, error, writing: writing > 0 };
}
